import React, { useState, useEffect } from 'react';
import { useClients, PIPELINE_STAGES } from '../context/ClientsContext';
import { useUsers, ROLE_LABELS } from '../context/UsersContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import StyledSelect from './StyledSelect';

export default function SaveEmployerDialog({ isOpen, onClose, onSave }) {
  const { currentClient, clients, loadClient, saveClient } = useClients();
  const { currentUser, isSuperUser, getActiveUsers, loadUsers, users } = useUsers();
  
  const [mode, setMode] = useState('create'); // 'select' or 'create'
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [stage, setStage] = useState('lead');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  
  const activeUsers = getActiveUsers();
  
  // Load users list on mount if Super User (for assignment dropdown)
  useEffect(() => {
    if (isSuperUser() && users.length === 0) {
      loadUsers();
    }
  }, [isSuperUser, users.length, loadUsers]);
  
  // Sync state when currentClient changes or dialog opens
  useEffect(() => {
    if (isOpen && currentClient) {
      // Updating existing client
      setMode('create');
      setClientName(currentClient.company?.legalName || '');
      setStage(currentClient.crm?.stage || 'lead');
      setCity(currentClient.company?.address?.city || '');
      setState(currentClient.company?.address?.state || '');
      setAssignedTo(currentClient.crm?.assignedTo || currentUser.id);
    } else if (isOpen) {
      // No client selected - show select/create options
      setMode(clients.length > 0 ? 'select' : 'create');
      setSelectedClientId('');
      setClientName('');
      setStage('lead');
      setCity('');
      setState('');
      setAssignedTo(currentUser.id);
    }
  }, [isOpen, currentClient, currentUser, clients]);
  
  const handleSave = async () => {
    if (mode === 'select') {
      // Load existing client
      if (!selectedClientId) {
        alert('Please select a client');
        return;
      }
      
      const client = await loadClient(selectedClientId);
      if (onSave) {
        onSave(client);
      }
      onClose();
    } else {
      // Create/update client
      if (!clientName.trim()) {
        alert('Please enter a client name');
        return;
      }
      
      const savedClient = await saveClient({
        ...currentClient,
        company: {
          ...(currentClient?.company || {}),
          legalName: clientName.trim(),
          address: {
            ...(currentClient?.company?.address || {}),
            city: city.trim(),
            state: state.trim().toUpperCase(),
          },
        },
        crm: {
          ...(currentClient?.crm || {}),
          stage,
          assignedTo: assignedTo || currentUser.id,
        },
      });
      
      if (onSave) {
        onSave(savedClient);
      }
      
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  const isUpdate = currentClient?.id;
  const employeeCount = currentClient?.employees?.length || 0;
  
  return (
    <div 
      onClick={(e) => {
        e.preventDefault();
        onClose();
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}>
      <div 
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        style={{
          ...S.card,
          width: '100%',
          maxWidth: 500,
          padding: '24px 28px',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>
            {isUpdate ? 'Update Client' : 'Save Client'}
          </h2>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
              touchAction: 'manipulation',
            }}
          >
            <Icon name="close" style={{ fontSize: 20, color: C.textMid }} />
          </button>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Mode selector (only show if no current client) */}
          {!isUpdate && clients.length > 0 && (
            <div>
              <label style={{ ...S.label, marginBottom: 8 }}>Select Action</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setMode('select')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: mode === 'select' ? C.accentLight : C.bg,
                    border: `1px solid ${mode === 'select' ? C.accent : C.border}`,
                    borderRadius: 8,
                    color: mode === 'select' ? C.accent : C.textMid,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Select Existing Client
                </button>
                <button
                  type="button"
                  onClick={() => setMode('create')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: mode === 'create' ? C.accentLight : C.bg,
                    border: `1px solid ${mode === 'create' ? C.accent : C.border}`,
                    borderRadius: 8,
                    color: mode === 'create' ? C.accent : C.textMid,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Create New Client
                </button>
              </div>
            </div>
          )}
          
          {/* Select existing client */}
          {mode === 'select' && (
            <div>
              <label style={{ ...S.label, marginBottom: 6 }}>
                Select Client <span style={{ color: C.red }}>*</span>
              </label>
              <StyledSelect
                value={selectedClientId || undefined}
                onValueChange={setSelectedClientId}
                options={clients.map(client => ({
                  value: client.id,
                  label: client.company?.legalName || client.company?.dba || 'Unnamed Client'
                }))}
                placeholder="-- Choose a client --"
                style={{ fontSize: 13 }}
              />
            </div>
          )}
          
          {/* Create new client form */}
          {mode === 'create' && (
            <>
              {/* Client Name */}
              <div>
                <label htmlFor="save-client-name" style={{ ...S.label, marginBottom: 6 }}>
                  Company Name <span style={{ color: C.red }}>*</span>
                </label>
                <input
                  id="save-client-name"
                  name="company-name"
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g., Acme Corporation"
                  autoComplete="organization"
                  style={{ ...S.input, fontSize: 13 }}
                  autoFocus
                />
              </div>
          
              {/* Location */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label htmlFor="save-client-city" style={{ ...S.label, marginBottom: 6 }}>City</label>
                  <input
                    id="save-client-city"
                    name="city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Los Angeles"
                    autoComplete="address-level2"
                    style={{ ...S.input, fontSize: 13 }}
                  />
                </div>
                <div>
                  <label htmlFor="save-client-state" style={{ ...S.label, marginBottom: 6 }}>State</label>
                  <input
                    id="save-client-state"
                    name="state"
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="CA"
                    maxLength={2}
                    autoComplete="address-level1"
                    style={{ ...S.input, fontSize: 13, textTransform: 'uppercase' }}
                  />
                </div>
              </div>
          
              {/* Stage */}
              <div>
                <label style={{ ...S.label, marginBottom: 6 }}>Pipeline Stage</label>
                <StyledSelect
                  value={stage}
                  onValueChange={setStage}
                  options={Object.entries(PIPELINE_STAGES).map(([key, stageInfo]) => ({
                    value: key,
                    label: stageInfo.label
                  }))}
                  style={{ fontSize: 13 }}
                />
              </div>
          
              {/* Assigned To */}
              <div>
                <label style={{ ...S.label, marginBottom: 6 }}>
                  Assigned To
                </label>
                <StyledSelect
                  value={assignedTo || currentUser.id}
                  onValueChange={setAssignedTo}
                  options={
                    activeUsers.length > 0
                      ? activeUsers.map(user => ({
                          value: user.id,
                          label: `${user.firstName} ${user.lastName} (${ROLE_LABELS[user.role]})`
                        }))
                      : [{ value: currentUser.id, label: `${currentUser.firstName} ${currentUser.lastName} (You)` }]
                  }
                  style={{ fontSize: 13 }}
                />
              </div>
          
              {/* Employee Count (if exists) */}
              {employeeCount > 0 && (
                <div style={{
                  padding: 12,
                  background: C.bg,
                  borderRadius: 6,
                  fontSize: 12,
                  color: C.textMid,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <Icon name="people-outline" style={{ fontSize: 16 }} />
                  <span>
                    {currentClient?.summary?.eligible || 0} eligible of {employeeCount} total employees
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              color: C.textMid,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
              touchAction: 'manipulation',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            style={{
              padding: '10px 18px',
              background: C.green,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
              touchAction: 'manipulation',
            }}
          >
            {isUpdate ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
